"use client";

import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export type AnalyticsUser = {
  id?: string;
  anonymous?: boolean;
  emailVerified?: boolean;
  managedWallet?: boolean;
};

export type AnalyticsOptions = {
  amplitude: {
    apiKey: string;
    enabled: boolean;
    serverUrl?: string;
  };
  ga: {
    measurementId: string;
    enabled: boolean;
  };
};

type AnalyticsTarget = "Amplitude" | "GA";

export type AnalyticsEvent =
  | "social_login_init"
  | "email_login_init"
  | "terms_link_clk"
  | "privacy_policy_link_clk"
  | "wrong_email_clk"
  | "resend_code_clk"
  | "password_auth_submit"
  | "captcha_abandoned"
  | "connect_wallet"
  | "connect_managed_wallet"
  | "disconnect_wallet"
  | "successful_tx"
  | "failed_tx"
  | "add_runtime_hours"
  | "remove_runtime_limit"
  | "close_deployment"
  | "use_depositor"
  | "downloaded_logs"
  | "update_deployment"
  | "downloaded_shell_file"
  | "create_lease"
  | "send_manifest"
  | "create_deployment"
  | "create_gpu_deployment"
  | "authorize_spend"
  | "navigate_tab"
  | "deploy_sdl"
  /** Dormant until template saving returns to the configure page (CON-673). */
  | "create_sdl_template"
  | "create_sdl_template_link"
  /** Dormant until template saving returns to the configure page (CON-673). */
  | "update_sdl_template"
  | "click_sdl_profile"
  | "click_view_template"
  | "save_sdl_description"
  | "add_sdl_favorite"
  | "remove_sdl_favorite"
  | "click_edit_sdl_template"
  | "user_profile_click_template"
  | "user_profile_template_tab"
  | "user_settings_save"
  | "anonymous_user_created"
  | "account_created"
  | "trial_completed"
  | "create_api_key"
  | "delete_api_key"
  | "close_deposit_modal"
  | "buy_credits_btn_clk"
  | "send_verification_code_btn_clk"
  | "builder_mode_btn_clk"
  | "yml_mode_btn_clk"
  | "bid_selected"
  | "bids_received"
  | "bids_not_received"
  | "bids_received_after_timeout"
  | "filtered_by_favorite_providers"
  | "filtered_by_audited_providers"
  | "close_deployment_btn_clk"
  | "build_n_deploy_btn_clk"
  | "launch_container_vm_btn_clk"
  | "run_custom_container_btn_clk"
  | "deploy_with_agent_btn_clk"
  | "sdl_uploaded"
  | "add_runtime_hours_btn_clk"
  | "remove_runtime_limit_btn_clk"
  | "add_funds_btn_clk"
  | "add_funds_coupon_btn_clk"
  | "add_funds_coupon_claim_amount_btn_clk"
  | "redeploy_btn_clk"
  | "redeploy_last_configuration_btn_clk"
  | "edit_name_btn_clk"
  | "create_deployment_btn_clk"
  | "log_collector_enabled"
  | "log_collector_disabled"
  | "log_collector_deployed"
  | "onboarding_intent_selected"
  | "get_started_step_click"
  | "get_started_dismissed"
  | "akashml_link_click"
  | "onboarding_setup_copied"
  | "onboarding_deploy_click"
  | "onboarding_choose_provider_click"
  | "onboarding_match_failed"
  | "onboarding_skipped"
  | "onboarding_left"
  | "akashml_link_click"
  | "trial_gpu_alternative_click"
  | "fair_use_policy_accepted"
  | "github_star_prompt_shown"
  | "github_star_prompt_clicked"
  | "github_star_prompt_dismissed"
  | "add_credits_opened"
  | "add_credits_payment_form_ready"
  | "add_credits_payment_form_load_failed"
  | "add_credits_amount_selected"
  | "add_credits_payment_method_selected"
  | "add_credits_payment_submitted"
  | "add_credits_payment_failed"
  | "add_credits_3ds_required"
  | "add_credits_3ds_completed"
  | "add_credits_purchased"
  | "add_credits_cancelled"
  | "add_credits_resumed_deploy"
  | "add_credits_next_step_clicked"
  | "configure_page_viewed"
  | "deployment_copies_checked"
  | "configure_preset_selected"
  | "configure_gpu_type_selected"
  | "configure_gpu_count_changed"
  | "configure_cpu_count_changed"
  | "configure_cpu_arch_changed"
  | "configure_sdl_imported"
  | "configure_sdl_downloaded"
  | "configure_sdl_copied"
  | "configure_reset_confirmed"
  | "configure_choose_provider_clicked"
  | "configure_choose_provider_blocked"
  | "configure_trial_size_fitted"
  | "configure_ssh_key_step_opened"
  | "configure_ssh_key_step_completed"
  | "configure_ssh_key_step_dismissed"
  | "create_deployment_failed"
  | "configure_request_compute_clicked"
  | "configure_not_bidding_warning_shown"
  | "configure_edit_clicked"
  | "configure_leave_discarded"
  | "cancel_during_create"
  | "close_deployment_failed"
  | "deploy_failed"
  | "review_deploy_opened"
  | "review_deploy_confirmed"
  | "review_deploy_dismissed"
  | "account_deletion_modal_opened"
  | "account_deletion_support_clicked"
  | "account_deletion_forfeit_chosen"
  | "command_menu_opened"
  | "command_menu_action_ran"
  | "whats_new_opened"
  | "whats_new_tour_started"
  | "whats_new_tour_ended"
  | "gpu_request_form_opened"
  | "gpu_request_sent"
  | "gpu_request_withdrawn"
  | "gpu_offer_accepted"
  | "gpu_offer_declined";

export type AnalyticsCategory = "user" | "billing" | "deployments" | "wallet" | "sdl_builder" | "transactions" | "profile" | "settings" | "onboarding";

export type EventProperties = {
  category?: AnalyticsCategory;
  [key: string]: unknown;
};

const GA_EVENTS = {
  successful_tx: "successful_transaction"
};

const AMPLITUDE_USER_PROPERTIES_MAP = {
  id: "user_id",
  anonymous: "is_anonymous",
  emailVerified: "is_email_verified",
  managedWallet: "managed_wallet"
};

const AMPLITUDE_PAGE_VIEWED_EVENT = "[Amplitude] Page Viewed";
const AMPLITUDE_PAGE_TITLE_PROPERTY = "[Amplitude] Page Title";
const AMPLITUDE_LOCATION_PROPERTIES = ["[Amplitude] Page Path", "[Amplitude] Page URL", "[Amplitude] Page Location"] as const;

/**
 * Deployment detail views embed the deployment sequence in both the page title (`Deployment detail #12345 |
 * Akash Console`, sometimes digit-masked by Amplitude as `#*****5`) and the URL (`/deployments/12345`), so the
 * inbuilt page-view event records a unique title and path per deployment and its views cannot be aggregated.
 * Collapsing the sequence to a static value lets every deployment detail view be filtered as a single page.
 */
const DEPLOYMENT_SEQUENCE_TITLE_MARKER = / #[\d*]+/;
const DEPLOYMENT_SEQUENCE_PATH = /\/deployments\/\d+/g;
const STATIC_DEPLOYMENT_PATH = "/deployments/[dseq]";

const UTM_PARAM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

/** Amplitude's own attribution reads the referrer only when it initializes, which is later than the visit it should credit. */
const FIRST_TOUCH_REFERRER_PROPERTY = "first_touch_referring_domain";

/** A direct visit is recorded too, so the identity provider a social login bounces through cannot claim the first touch. */
const DIRECT_VISIT_REFERRER = "";

const isBrowser = typeof window !== "undefined";

/**
 * Hosts that never belong to a real user: dev servers and jsdom test runs both report one of these, and every
 * device they invent is billed as a tracked user against the Amplitude MTU quota even though nobody is using the app.
 */
const UNTRACKABLE_HOSTNAMES = new Set(["localhost", "0.0.0.0", "::1", "[::1]"]);

/** The whole 127.0.0.0/8 range is loopback, so a dev server bound to any of it is still nobody. */
const IPV4_LOOPBACK = /^127(?:\.\d{1,3}){3}$/;

function isTrackableHostname(hostname: string): boolean {
  if (!hostname || UNTRACKABLE_HOSTNAMES.has(hostname) || IPV4_LOOPBACK.test(hostname)) {
    return false;
  }

  return !hostname.endsWith(".localhost") && !hostname.endsWith(".local");
}

export type Amplitude = Pick<typeof amplitude, "init" | "Identify" | "identify" | "track" | "setUserId" | "add" | "flush">;

export class AnalyticsService {
  private readonly STORAGE_KEY = "analytics_values_cache";
  private readonly UTM_STORAGE_KEY = "analytics_utm";
  private readonly REFERRER_STORAGE_KEY = "analytics_referrer";

  private readonly valuesCache: Map<string, string> = this.loadSwitchValuesFromStorage();

  private readonly isAmplitudeEnabled: boolean;
  private amplitudeInitialized = false;

  /** First-touch UTM params, stamped onto every tracked event so acquisition funnels can be attributed to a campaign. */
  private readonly utmProperties: Record<string, string>;

  /** First-touch referring domain, stamped onto every tracked event so acquisition source outlives Amplitude initializing after the visit that earned it. */
  private readonly referrerProperties: Record<string, string>;

  constructor(
    private readonly options: AnalyticsOptions,
    private readonly amplitudeClient: Amplitude = amplitude,
    private readonly getDataLayer: () => Record<string, unknown>[] | undefined = () => (isBrowser ? window.dataLayer : undefined),
    private readonly storage: Pick<Storage, "getItem" | "setItem"> | undefined = isBrowser ? window.localStorage : undefined,
    private readonly getLocationSearch: () => string = () => (isBrowser ? window.location.search : ""),
    private readonly getHostname: () => string = () => (isBrowser ? window.location.hostname : ""),
    private readonly getReferrer: () => string = () => (isBrowser ? document.referrer : "")
  ) {
    this.isAmplitudeEnabled = this.options.amplitude.enabled && !!this.options.amplitude.apiKey.trim() && isTrackableHostname(this.getHostname());
    this.utmProperties = this.captureFirstTouchUtm();
    this.referrerProperties = this.captureFirstTouchReferrer();
  }

  /**
   * Captures `utm_*` from the first UTM-bearing visit and freezes it: once a snapshot is stored it is returned
   * unchanged, so a later visit from a different campaign can't blend its params into the original acquisition touch.
   */
  private captureFirstTouchUtm(): Record<string, string> {
    if (!isBrowser) {
      return {};
    }

    const storedUtm = this.readStoredUtm();
    if (Object.keys(storedUtm).length > 0) {
      return storedUtm;
    }

    const urlUtm = this.readUrlUtm();
    if (Object.keys(urlUtm).length > 0) {
      this.storage?.setItem(this.UTM_STORAGE_KEY, JSON.stringify(urlUtm));
    }

    return urlUtm;
  }

  private readUrlUtm(): Record<string, string> {
    const params = new URLSearchParams(this.getLocationSearch());
    return this.pickUtmParams(key => params.get(key));
  }

  private readStoredUtm(): Record<string, string> {
    const stored = this.storage?.getItem(this.UTM_STORAGE_KEY);
    if (!stored) {
      return {};
    }

    try {
      const parsed = JSON.parse(stored) as Record<string, unknown>;
      return this.pickUtmParams(key => (typeof parsed[key] === "string" ? (parsed[key] as string) : null));
    } catch {
      return {};
    }
  }

  private pickUtmParams(getValue: (key: string) => string | null): Record<string, string> {
    const utm: Record<string, string> = {};
    for (const key of UTM_PARAM_KEYS) {
      const value = getValue(key);
      if (value) {
        utm[key] = value;
      }
    }
    return utm;
  }

  private captureFirstTouchReferrer(): Record<string, string> {
    if (!isBrowser) {
      return {};
    }

    const storedDomain = this.storage?.getItem(this.REFERRER_STORAGE_KEY);
    if (storedDomain != null) {
      return this.toReferrerProperties(storedDomain);
    }

    const referringDomain = this.readExternalReferringDomain() ?? DIRECT_VISIT_REFERRER;
    this.persistFirstTouchReferrer(referringDomain);

    return this.toReferrerProperties(referringDomain);
  }

  /** Every new session writes this one, so a full or blocked storage must cost the stored first touch rather than the constructor. */
  private persistFirstTouchReferrer(referringDomain: string) {
    try {
      this.storage?.setItem(this.REFERRER_STORAGE_KEY, referringDomain);
    } catch {
      return;
    }
  }

  private toReferrerProperties(referringDomain: string): Record<string, string> {
    return referringDomain ? { [FIRST_TOUCH_REFERRER_PROPERTY]: referringDomain } : {};
  }

  /** Our own host means an internal navigation, which would otherwise overwrite the source that actually brought the visitor in. */
  private readExternalReferringDomain(): string | undefined {
    const referrer = this.getReferrer();
    if (!referrer) {
      return undefined;
    }

    try {
      const { hostname } = new URL(referrer);
      return hostname && hostname !== this.getHostname() ? hostname : undefined;
    } catch {
      return undefined;
    }
  }

  private loadSwitchValuesFromStorage() {
    if (typeof window !== "undefined") {
      const stored = this.storage?.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Map(Object.entries(parsed));
      }
    }

    return new Map();
  }

  identify(user: AnalyticsUser): void {
    if (!isBrowser || !Object.keys(user).length) {
      return;
    }

    if (this.options.ga.enabled && user.id) {
      this.getDataLayer()?.push({ user_id: user.id });
    }

    if (!this.isAmplitudeEnabled) {
      return;
    }

    this.initAmplitude();
    const event = new this.amplitudeClient.Identify();

    for (const key in user) {
      if (key !== "id") {
        event.set(AMPLITUDE_USER_PROPERTIES_MAP[key as keyof AnalyticsUser] || key, String(user[key as keyof typeof user]));
      }
    }

    this.amplitudeClient.identify(event);

    if (user.id) {
      this.amplitudeClient.setUserId(user.id);
    }
  }

  /** Initializing mints a billed tracked user and starts a session replay. */
  private initAmplitude() {
    if (this.amplitudeInitialized) {
      return;
    }

    const { serverUrl } = this.options.amplitude;
    const initOptions = serverUrl ? { serverUrl } : undefined;

    const sessionReplayTracking = sessionReplayPlugin();
    this.amplitudeClient.add(sessionReplayTracking);
    this.amplitudeClient.add(createStaticDeploymentPageViewPlugin());

    this.amplitudeClient.init(this.options.amplitude.apiKey, undefined, initOptions);
    this.amplitudeInitialized = true;
    this.setFirstTouchReferrerUserProperty();
  }

  private setFirstTouchReferrerUserProperty() {
    const referringDomain = this.referrerProperties[FIRST_TOUCH_REFERRER_PROPERTY];
    if (!referringDomain) {
      return;
    }

    const event = new this.amplitudeClient.Identify();
    event.setOnce(FIRST_TOUCH_REFERRER_PROPERTY, referringDomain);
    this.amplitudeClient.identify(event);
  }

  trackSwitch(eventName: "connect_wallet", value: "managed", target?: AnalyticsTarget): void;
  trackSwitch(eventName: AnalyticsEvent, value: string, target?: AnalyticsTarget) {
    if (!isBrowser) {
      return;
    }

    if (this.valuesCache.get(eventName) === value) {
      return;
    }

    this.saveSwitchValue(eventName, value);

    return this.track(eventName, { value }, target);
  }

  private saveSwitchValue(eventName: string, value: string) {
    this.valuesCache.set(eventName, value);
    const obj = Object.fromEntries(this.valuesCache);
    this.storage?.setItem(this.STORAGE_KEY, JSON.stringify(obj));
  }

  track(eventName: AnalyticsEvent, target?: AnalyticsTarget): void;
  track(eventName: AnalyticsEvent, eventProperties: EventProperties, target?: AnalyticsTarget): void;
  track(eventName: AnalyticsEvent, eventPropertiesOrTarget?: EventProperties | AnalyticsTarget, target?: AnalyticsTarget): void {
    if (!isBrowser) {
      return;
    }

    const analyticsTarget = typeof eventPropertiesOrTarget === "string" ? eventPropertiesOrTarget : target;
    const eventProperties = typeof eventPropertiesOrTarget === "object" ? eventPropertiesOrTarget : {};
    const enrichedProperties = { ...this.utmProperties, ...this.referrerProperties, ...eventProperties };

    if (this.isAmplitudeEnabled && (!analyticsTarget || analyticsTarget === "Amplitude")) {
      this.initAmplitude();
      this.amplitudeClient.track(eventName, enrichedProperties);
    }

    if (this.options.ga.enabled && (!analyticsTarget || analyticsTarget === "GA")) {
      const [name, props] = this.transformGaEvent(eventName, enrichedProperties);
      this.getDataLayer()?.push({ ...props, event: name });
    }
  }

  flush(): void {
    if (!isBrowser || !this.isAmplitudeEnabled) {
      return;
    }

    this.amplitudeClient.flush();
  }

  private transformGaEvent(eventName: AnalyticsEvent, eventProperties: EventProperties): [string, Record<string, unknown>] {
    if (eventName === "navigate_tab") {
      return [`${eventName}_${eventProperties.tab}`, eventProperties];
    }

    return [GA_EVENTS[eventName as keyof typeof GA_EVENTS] || eventName, eventProperties];
  }
}

function createStaticDeploymentPageViewPlugin(): amplitude.Types.EnrichmentPlugin {
  return {
    name: "static-deployment-page-view",
    type: "enrichment",
    execute: async event => {
      if (event.event_type !== AMPLITUDE_PAGE_VIEWED_EVENT) {
        return event;
      }

      const properties = event.event_properties;
      if (!properties) {
        return event;
      }

      const title = properties[AMPLITUDE_PAGE_TITLE_PROPERTY];
      if (typeof title === "string") {
        properties[AMPLITUDE_PAGE_TITLE_PROPERTY] = title.replace(DEPLOYMENT_SEQUENCE_TITLE_MARKER, "");
      }

      for (const property of AMPLITUDE_LOCATION_PROPERTIES) {
        const value = properties[property];
        if (typeof value === "string") {
          properties[property] = value.replace(DEPLOYMENT_SEQUENCE_PATH, STATIC_DEPLOYMENT_PATH);
        }
      }

      return event;
    }
  };
}
